import axios from '../axios';
import {
  GET_KITCHENS_BY_MANAGING_BRANCH,
  REGIONAL_UPDATE_KITCHEN_OPERATION,
} from '../ApiUrl';

/** Address object from GetKitchensByManagingBranch `data[]` (nested `address` + `Geolocation`). */
function formatAddressFromKitchenItem(item) {
  const { address: line, Geolocation } = item.address ?? {};
  const zipRaw = Geolocation?.zipcode;
  const zip = zipRaw != null && String(zipRaw).trim() !== '' ? String(zipRaw).trim() : '';
  const trimmedLine = line?.trim();
  if (trimmedLine) {
    return zip ? `${trimmedLine} · ${zip}` : trimmedLine;
  }
  return Geolocation?.formattedAddress?.trim() || zip || '';
}

/**
 * Row status from API `status` boolean only — no derived “pending” labels.
 */
function statusFromApiItem(item) {
  if (typeof item.status === 'boolean') {
    return item.status ? 'Active' : 'Inactive';
  }
  return '—';
}

/** Known API values for `store_visibility_status`. */
export const STORE_VISIBILITY_VALUES = ['live', 'under_review', 'under_testing'];

/**
 * Normalizes `store_visibility_status` when the API sends it; otherwise empty.
 */
export function normalizeStoreVisibilityStatus(raw) {
  if (raw == null || raw === '') return '';
  const n = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
  if (n === 'live') return 'live';
  if (n === 'under_review') return 'under_review';
  if (n === 'under_testing') return 'under_testing';
  return n;
}

export function getStoreVisibilityLabel(canonical) {
  if (canonical == null || canonical === '') return '—';
  switch (canonical) {
    case 'live':
      return 'Live';
    case 'under_review':
      return 'Under review';
    case 'under_testing':
      return 'Under testing';
    default:
      return String(canonical)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

function kitchenDetailFromApiItem(item) {
  const direct = item.kitchen_detail ?? item.kitchenDetail;
  if (typeof direct === 'string' && direct.trim()) {
    return direct.trim();
  }
  const email = item.email != null ? String(item.email).trim() : '';
  return email || '';
}

function parseOnboardingBool(value) {
  if (value == null || value === '') return null;
  if (value === true || value === 'true' || value === '1' || value === 1) return true;
  if (value === false || value === 'false' || value === '0' || value === 0) return false;
  return Boolean(value);
}

function onboardingStatusFromApiItem(item) {
  const raw =
    item?.kitchen_operation?.onborading_status ?? item?.kitchen_operation?.onboarding_status;
  if (!raw || typeof raw !== 'object') {
    return {
      einMenuUploaded: null,
      menuVerified: null,
      deviceAssigned: null,
      dedicatedManagerAssigned: null,
    };
  }
  return {
    einMenuUploaded: parseOnboardingBool(raw.ein_menu_uploaded),
    menuVerified: parseOnboardingBool(raw.menu_verified),
    deviceAssigned: parseOnboardingBool(raw.device_assigned),
    dedicatedManagerAssigned: parseOnboardingBool(raw.dedicated_manager_assigned),
  };
}

/**
 * Maps one kitchen from GetKitchensByManagingBranch `data[]` to the table row shape.
 */
export function mapKitchenApiToRow(item, index) {
  const id = String(item._id ?? `idx-${index}`);
  const name = item.kitchenName != null ? String(item.kitchenName) : '—';
  const visRaw = item.store_visibility_status ?? item.storeVisibilityStatus;
  const storeVisibilityStatus =
    visRaw != null && String(visRaw).trim() !== ''
      ? normalizeStoreVisibilityStatus(visRaw)
      : '';
  const onboardingStatus = onboardingStatusFromApiItem(item);

  return {
    id,
    kitchenName: name,
    kitchenDetail: kitchenDetailFromApiItem(item),
    address: formatAddressFromKitchenItem(item),
    products: Number(item.total_products) || 0,
    storeVisibilityStatus,
    status: statusFromApiItem(item),
    einMenuUploaded: onboardingStatus.einMenuUploaded,
    menuVerified: onboardingStatus.menuVerified,
    deviceAssigned: onboardingStatus.deviceAssigned,
    dedicatedManagerAssigned: onboardingStatus.dedicatedManagerAssigned,
    zipcode: item?.address?.Geolocation?.zipcode,
    rawData: item,
  };
}

/**
 * POST /GetKitchensByManagingBranch
 * Body: { managing_branch_name, page?, limit?, store_visibility_status?, status?, kitchen_name_search? }
 * Response interceptor wraps as { hasError, data: { success, total_kitchens, data, ... } }
 */
export const getKitchensByManagingBranchAPI = async ({
  managing_branch_name,
  page = 1,
  limit = 20,
  store_visibility_status,
  status,
  kitchen_name_search,
}) => {
  try {
    const name = String(managing_branch_name ?? '')
      .trim()
      .toLowerCase()
      .slice(0, 50);
    const safePage = Math.max(1, parseInt(String(page), 10) || 1);
    let safeLimit = parseInt(String(limit), 10) || 20;
    if (safeLimit < 1) safeLimit = 1;
    if (safeLimit > 100) safeLimit = 100;

    const body = {
      managing_branch_name: name,
      page: safePage,
      limit: safeLimit,
    };

    const search = kitchen_name_search != null ? String(kitchen_name_search).trim() : '';
    if (search !== '') {
      body.kitchen_name_search = search;
    }

    if (typeof status === 'boolean') {
      body.status = status;
    }

    const vis =
      store_visibility_status != null && String(store_visibility_status).trim() !== ''
        ? String(store_visibility_status).trim().toLowerCase()
        : '';
    if (vis !== '' && vis !== 'all') {
      body.store_visibility_status = normalizeStoreVisibilityStatus(vis) || vis;
    }

    const response = await axios.post(GET_KITCHENS_BY_MANAGING_BRANCH, body);
    return response;
  } catch (error) {
    console.log('getKitchensByManagingBranchAPI catch', error);
    return { hasError: true, message: error?.message || 'Failed to load kitchens.' };
  }
};

/** Maps table row / API account status to Active | Inactive label. */
export function accountStatusLabelFromRow(row) {
  if (row?.status === 'Active' || row?.status === 'Inactive') {
    return row.status;
  }
  const raw = row?.rawData?.status;
  if (typeof raw === 'boolean') return raw ? 'Active' : 'Inactive';
  return 'Active';
}

export function accountStatusLabelToBoolean(label) {
  return String(label).toLowerCase() === 'active';
}

/**
 * PUT /RegionalUpdateKitchenOperation
 * Body: kitchen_name, managing_branch_name, and at least one of status, store_visibility_status, …
 */
export const regionalUpdateKitchenOperationAPI = async (body) => {
  try {
    return await axios.put(REGIONAL_UPDATE_KITCHEN_OPERATION, body);
  } catch (error) {
    console.log('regionalUpdateKitchenOperationAPI catch', error);
    return { hasError: true, message: error?.message || 'Failed to update store status.' };
  }
};

/** Normalizes API error payloads for inline form display. */
export function mapKitchenOperationUpdateErrorsToForm(body) {
  if (!body || typeof body !== 'object') {
    return [{ errorMessage: 'Update failed.', param: 'status_update' }];
  }
  const rawList = body.errors;
  if (Array.isArray(rawList) && rawList.length > 0) {
    return rawList.map((e) => ({
      errorMessage:
        e?.errorMessage ?? e?.message ?? e?.msg ?? e?.CustomMessage ?? String(e?.error ?? 'Validation error'),
      param: e?.param ?? e?.field ?? 'field',
    }));
  }
  if (body.success === false) {
    const msg =
      body.CustomMessage ??
      body.message ??
      body.customMessage ??
      (typeof body.errors === 'object' && !Array.isArray(body.errors)
        ? body.errors?.errorMessage
        : null) ??
      'Could not update store status.';
    return [{ errorMessage: msg, param: 'status_update' }];
  }
  return [{ errorMessage: 'Update failed.', param: 'status_update' }];
}
